import { VisualizationModule } from '@core/models/data-visualization/data-visualization.model';
import {
  AuxiliaryDataFetchDetails,
  EntityCfg,
  EntityInterface,
  TableColCfg
} from '@core/models/entities/entity-visualization-interface';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { CustomData } from '@core/models/entities/implementations/custom-data';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';

import { FilterDataType } from '@shared/components/filter-query-builder/filter.models';

import { UserEntityRegistrations, renderRegistrations } from './user-entity-registrations';

// Simplified UserGeoprofileCfg interface - adjust as needed based on actual requirements
export type UserGeoprofileCfg = object;

export interface UserVehicleV1Model {
  uid: string;
  geo_profile_cfg: UserGeoprofileCfg; // Default empty
  co2_g_per_km?: number; // Optional in Rust
  fixed_cost: number;
  cost_per_kilometer: number;
  cost_per_work_hour: number;
  cost_per_wait_hour: number;
  cost_per_stop: number;
  route_start_duration: string; // ISO8601 duration string
  setup_duration_outside_site: string; // ISO8601 duration string
  setup_duration_inside_site: string; // ISO8601 duration string
  route_finish_duration: string; // ISO8601 duration string
  start_site_uid?: string; // Optional in Rust
  finish_site_uid?: string; // Optional in Rust
  is_external_fleet: boolean; // Default false in Rust
  max_nb_stops?: number;
  autonomy_meters?: number;
  travel_speed_factor?: number;
  registrations: UserEntityRegistrations;
  compatible_trailer_uids?: string[];
  compatible_driver_uids?: string[];
  company_uid?: string;
  custom_data: CustomData;
}
type RawModel = UserVehicleV1Model;

export class UserVehicleCfg implements EntityCfg<RawModel> {
  static readonly visus = new Set<VisualizationModule>([]);

  supportedVisualizations(): Set<VisualizationModule> {
    return UserVehicleCfg.visus;
  }

  versionName(): string {
    return 'UserVehicle';
  }

  defaultSortingCol(): string[] {
    return ['name'];
  }

  availableDetailEntityType(): string[] {
    return [];
  }

  makeAuxDataFetchDetails(_entities: RawModel[]): AuxiliaryDataFetchDetails {
    return {
      versionNameAndUidsPerType: {}
    };
  }

  buildColumns(): TableColCfg<EntityInterface<RawModel>>[] {
    const cols: TableColCfg<EntityInterface<RawModel>>[] = [
      {
        key: 'raw.uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.uid',
        type: 'string',
        sortCfg: { dbColName: 'uid' },
        filterCfg: {
          translationLabel: 'uid',
          dbColName: 'uid',
          filterType: FilterDataType.TEXT
        }
      },
      {
        key: 'raw.name' as keyof EntityInterface<RawModel>,
        label: 'table.columns.name',
        type: 'string',
        sortCfg: { dbColName: 'name' },
        filterCfg: {
          translationLabel: 'name',
          dbColName: 'name',
          filterType: FilterDataType.TEXT,
          supportRegexFiltering: true
        }
      },
      {
        key: 'raw.co2_g_per_km' as keyof EntityInterface<RawModel>,
        label: 'table.columns.co2_g_per_km',
        type: 'number'
      },
      {
        key: 'raw.fixed_cost' as keyof EntityInterface<RawModel>,
        label: 'table.columns.fixed_cost',
        type: 'number'
      },
      {
        key: 'raw.cost_per_kilometer' as keyof EntityInterface<RawModel>,
        label: 'table.columns.cost_per_kilometer',
        type: 'number'
      },
      {
        key: 'raw.cost_per_work_hour' as keyof EntityInterface<RawModel>,
        label: 'table.columns.cost_per_work_hour',
        type: 'number'
      },
      {
        key: 'raw.cost_per_wait_hour' as keyof EntityInterface<RawModel>,
        label: 'table.columns.cost_per_wait_hour',
        type: 'number'
      },
      {
        key: 'raw.cost_per_stop' as keyof EntityInterface<RawModel>,
        label: 'table.columns.cost_per_stop',
        type: 'number'
      },
      {
        key: 'raw.route_start_duration' as keyof EntityInterface<RawModel>,
        label: 'table.columns.route_start_duration',
        type: 'duration'
      },
      {
        key: 'raw.setup_duration_outside_site' as keyof EntityInterface<RawModel>,
        label: 'table.columns.setup_duration_outside_site',
        type: 'duration'
      },
      {
        key: 'raw.setup_duration_inside_site' as keyof EntityInterface<RawModel>,
        label: 'table.columns.setup_duration_inside_site',
        type: 'duration'
      },
      {
        key: 'raw.route_finish_duration' as keyof EntityInterface<RawModel>,
        label: 'table.columns.route_finish_duration',
        type: 'duration'
      },
      {
        key: 'raw.start_site_uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.start_site',
        type: 'uid',
        referenceEntityType: 'SITE',
        sortCfg: { dbColName: 'start_site_uid' }
      },
      {
        key: 'raw.finish_site_uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.finish_site',
        type: 'uid',
        referenceEntityType: 'SITE',
        sortCfg: { dbColName: 'finish_site_uid' }
      },
      {
        key: 'raw.is_external_fleet' as keyof EntityInterface<RawModel>,
        label: 'table.columns.is_external_fleet',
        type: 'boolean',
        sortCfg: { dbColName: 'is_external_fleet' }
      },
      {
        key: 'raw.max_nb_stops' as keyof EntityInterface<RawModel>,
        label: 'table.columns.max_nb_stops',
        type: 'number'
      },
      {
        key: 'raw.autonomy_meters' as keyof EntityInterface<RawModel>,
        label: 'table.columns.autonomy_meters',
        type: 'number'
      },
      {
        key: 'raw.travel_speed_factor' as keyof EntityInterface<RawModel>,
        label: 'table.columns.travel_speed_factor',
        type: 'number'
      },
      {
        key: 'raw.registrations' as keyof EntityInterface<RawModel>,
        label: 'table.columns.registrations',
        type: 'string',
        renderHook: (value) => renderRegistrations(value as UserEntityRegistrations)
      },
      {
        key: 'raw.compatible_trailer_uids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.compatible_trailers',
        type: 'uid-list',
        referenceEntityType: 'TRAILER'
      },
      {
        key: 'raw.compatible_driver_uids' as keyof EntityInterface<RawModel>,
        label: 'table.columns.compatible_drivers',
        type: 'uid-list',
        referenceEntityType: 'DRIVER'
      },
      {
        key: 'raw.company_uid' as keyof EntityInterface<RawModel>,
        label: 'table.columns.company',
        type: 'uid',
        sortCfg: { dbColName: 'company_uid' },
        filterCfg: {
          translationLabel: 'company',
          dbColName: 'company_uid',
          filterType: FilterDataType.UID,
          uidEntityType: 'COMPANY'
        }
      },
      {
        key: 'raw.custom_data.tags' as keyof EntityInterface<RawModel>,
        label: 'table.columns.tags',
        type: 'uid-list',
        filterCfg: {
          translationLabel: 'tags',
          dbColName: 'tags',
          filterType: FilterDataType.TAGS
        }
      },
      {
        key: 'raw.custom_data.labels' as keyof EntityInterface<RawModel>,
        label: 'table.columns.custom_labels',
        type: 'string-number-map',
        filterCfg: {
          translationLabel: 'custom_labels',
          dbColName: 'labels',
          filterType: FilterDataType.LABELS
        }
      }
    ];

    return cols;
  }
}

export function registerVehicleExtensions(registry: GenericEntityRegistryService): void {
  const overrides: EntityCfgOverrides<UserVehicleV1Model> = {
    buildColumns: (autoColumns) =>
      autoColumns.map((col) => {
        const key = col.key as string;
        if (key === 'raw.registrations') {
          return {
            ...col,
            renderHook: (value: unknown) =>
              value == null ? '' : renderRegistrations(value as UserEntityRegistrations)
          };
        }
        return col;
      })
  };
  registry.registerOverrides('UserVehicle', overrides);
}
